import type { ComputedRef } from 'vue';

import { AssetType, PlatformBusClass } from '~/apiClient';
import { Locale } from '~i18n/i18nConfig';

type LocalizedAssetTypes = Record<AssetType, string>;
type LocalizedClasses = Record<AssetType, Record<PlatformBusClass, string>>;

interface UseSlpRouteParamsParserReturn {
    localizedAssetTypes: ComputedRef<LocalizedAssetTypes>;
    localizedClasses: ComputedRef<LocalizedClasses>;
}

export const optionalParamsOrder: OptionalParamKey[] = ['localizedClass', 'brandUrlKey', 'modelUrlKey'];

export const optionalParamsLetter: Record<OptionalParamKey, string> = {
    localizedClass: 'c',
    brandUrlKey: 'b',
    modelUrlKey: 'm',
};

export const localizedParams = {
    [Locale.DE]: {
        assetType: {
            [AssetType.Bus]: 'bus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'stadtbus',
                [PlatformBusClass.Intercity]: 'ueberlandbus',
                [PlatformBusClass.Touristic]: 'sightseeing',
                [PlatformBusClass.SchoolBus]: 'schulbus',
            },
        },
    },
    [Locale.EN]: {
        assetType: {
            [AssetType.Bus]: 'bus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'city',
                [PlatformBusClass.Intercity]: 'intercity',
                [PlatformBusClass.Touristic]: 'sightseeing',
                [PlatformBusClass.SchoolBus]: 'school',
            },
        },
    },
    [Locale.ES]: {
        assetType: {
            [AssetType.Bus]: 'autobus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'urbano',
                [PlatformBusClass.Intercity]: 'interurbano',
                [PlatformBusClass.Touristic]: 'turistico',
                [PlatformBusClass.SchoolBus]: 'escolar',
            },
        },
    },
    [Locale.FR]: {
        assetType: {
            [AssetType.Bus]: 'bus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'urbain',
                [PlatformBusClass.Intercity]: 'interurbain',
                [PlatformBusClass.Touristic]: 'touristique',
                [PlatformBusClass.SchoolBus]: 'scolaire',
            },
        },
    },
    [Locale.IT]: {
        assetType: {
            [AssetType.Bus]: 'autobus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'urbano',
                [PlatformBusClass.Intercity]: 'interurbano',
                [PlatformBusClass.Touristic]: 'turistico',
                [PlatformBusClass.SchoolBus]: 'scuolabus',
            },
        },
    },
    [Locale.PL]: {
        assetType: {
            [AssetType.Bus]: 'autobusy',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'miejski',
                [PlatformBusClass.Intercity]: 'miedzymiastowy',
                [PlatformBusClass.Touristic]: 'wycieczkowy',
                [PlatformBusClass.SchoolBus]: 'szkolny',
            },
        },
    },
    [Locale.NL]: {
        assetType: {
            [AssetType.Bus]: 'bussen',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'stedelijk',
                [PlatformBusClass.Intercity]: 'interstedelijk',
                [PlatformBusClass.Touristic]: 'toeristisch',
                [PlatformBusClass.SchoolBus]: 'schoolbus',
            },
        },
    },
    [Locale.PT]: {
        assetType: {
            [AssetType.Bus]: 'autobus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'urbano',
                [PlatformBusClass.Intercity]: 'interurbano',
                [PlatformBusClass.Touristic]: 'turistico',
                [PlatformBusClass.SchoolBus]: 'escolar',
            },
        },
    },
    [Locale.CS]: {
        assetType: {
            [AssetType.Bus]: 'autobusy',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'mestsky',
                [PlatformBusClass.Intercity]: 'mezimestsky',
                [PlatformBusClass.Touristic]: 'turisticky',
                [PlatformBusClass.SchoolBus]: 'skolni-autobus',
            },
        },
    },
    [Locale.RO]: {
        assetType: {
            [AssetType.Bus]: 'autobuz',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'urban',
                [PlatformBusClass.Intercity]: 'interurban',
                [PlatformBusClass.Touristic]: 'turistic',
                [PlatformBusClass.SchoolBus]: 'autobuz-scolar',
            },
        },
    },
    [Locale.RU]: {
        assetType: {
            [AssetType.Bus]: 'avtobus',
        },
        class: {
            [AssetType.Bus]: {
                [PlatformBusClass.City]: 'gorodskoy',
                [PlatformBusClass.Intercity]: 'mezhdugorodnyy',
                [PlatformBusClass.Touristic]: 'turisticheskiy',
                [PlatformBusClass.SchoolBus]: 'shkolnyy',
            },
        },
    },
};

export const useSlpLocalizedRouteParams = (): UseSlpRouteParamsParserReturn => {
    const { locale } = useTypedI18n();

    const localizedAssetTypes = computed(() => localizedParams[locale.value as Locale].assetType);

    const localizedClasses = computed(() => localizedParams[locale.value as Locale].class);

    return {
        localizedAssetTypes,
        localizedClasses,
    };
};
